import React from 'react';
import './DocumentDetailModal.css';

const DocumentDetailModal = ({ document, type, onClose, onDownload, onResendEmail, onDelete, loading }) => {
  if (!document) return null;

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  };

  const isBillOfSale = type === 'bill';
  const hasSignatures = isBillOfSale && (document.sellerSignature || document.buyerSignature);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content document-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title-section">
            <h2>{isBillOfSale ? 'Bill of Sale' : 'Invoice'}</h2>
            <span className="document-number">
              {isBillOfSale ? document.documentNumber : document.invoiceNumber}
            </span>
          </div>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* Status Badge */}
          <div className="detail-status-row">
            <span className={`badge badge-lg ${
              document.status === 'completed' || document.status === 'paid' ? 'badge-success' :
              document.status === 'overdue' ? 'badge-danger' : 'badge-warning'
            }`}>
              {hasSignatures ? '✓ Signed' : document.status}
            </span>
            <span className="detail-date">Created {formatDate(document.createdAt)}</span>
          </div>

          {/* Bill of Sale Details */}
          {isBillOfSale && (
            <>
              {/* Parties */}
              <div className="detail-section">
                <h3>Parties</h3>
                <div className="detail-grid two-col">
                  <div className="detail-card">
                    <span className="detail-label">Seller</span>
                    <strong>{document.seller?.name}</strong>
                    {document.seller?.email && <p>{document.seller.email}</p>}
                    {document.seller?.phone && <p>{document.seller.phone}</p>}
                    {document.seller?.address?.street && (
                      <p className="detail-address">
                        {document.seller.address.street}<br />
                        {document.seller.address.city}, {document.seller.address.state} {document.seller.address.zipCode}
                      </p>
                    )}
                  </div>
                  <div className="detail-card">
                    <span className="detail-label">Buyer</span>
                    <strong>{document.buyer?.name}</strong>
                    {document.buyer?.email && <p>{document.buyer.email}</p>}
                    {document.buyer?.phone && <p>{document.buyer.phone}</p>}
                    {document.buyer?.address?.street && (
                      <p className="detail-address">
                        {document.buyer.address.street}<br />
                        {document.buyer.address.city}, {document.buyer.address.state} {document.buyer.address.zipCode}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              {/* Item */}
              <div className="detail-section">
                <h3>Item Details</h3>
                <div className="detail-card">
                  <div className="detail-row">
                    <span className="detail-label">Description</span>
                    <span>{document.item?.description}</span>
                  </div>
                  {document.item?.make && (
                    <div className="detail-row">
                      <span className="detail-label">Make/Model</span>
                      <span>{document.item.make} {document.item.model}</span>
                    </div>
                  )}
                  {document.item?.year && (
                    <div className="detail-row">
                      <span className="detail-label">Year</span>
                      <span>{document.item.year}</span>
                    </div>
                  )}
                  {document.item?.serialNumber && (
                    <div className="detail-row">
                      <span className="detail-label">Serial/VIN</span>
                      <span>{document.item.serialNumber}</span>
                    </div>
                  )}
                  {document.item?.condition && (
                    <div className="detail-row">
                      <span className="detail-label">Condition</span>
                      <span className="capitalize">{document.item.condition}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Transaction */}
              <div className="detail-section">
                <h3>Transaction</h3>
                <div className="detail-card highlight">
                  <div className="detail-row">
                    <span className="detail-label">Sale Price</span>
                    <span className="detail-price">{formatCurrency(document.transaction?.salePrice)}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Payment Method</span>
                    <span className="capitalize">{document.transaction?.paymentMethod?.replace('-', ' ')}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Sale Date</span>
                    <span>{formatDate(document.transaction?.saleDate)}</span>
                  </div>
                  {document.transaction?.location && (
                    <div className="detail-row">
                      <span className="detail-label">Location</span>
                      <span>{document.transaction.location}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Signatures */}
              {hasSignatures && (
                <div className="detail-section">
                  <h3>Signatures</h3>
                  <div className="detail-grid two-col">
                    {document.sellerSignature && (
                      <div className="detail-card signature-card">
                        <span className="detail-label">Seller Signature</span>
                        <img src={document.sellerSignature} alt="Seller signature" className="signature-preview" />
                        {document.sellerSignedAt && (
                          <span className="signature-date">Signed {formatDate(document.sellerSignedAt)}</span>
                        )}
                      </div>
                    )}
                    {document.buyerSignature && (
                      <div className="detail-card signature-card">
                        <span className="detail-label">Buyer Signature</span>
                        <img src={document.buyerSignature} alt="Buyer signature" className="signature-preview" />
                        {document.buyerSignedAt && (
                          <span className="signature-date">Signed {formatDate(document.buyerSignedAt)}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* Invoice Details */}
          {!isBillOfSale && (
            <>
              {/* Parties */}
              <div className="detail-section">
                <h3>Parties</h3>
                <div className="detail-grid two-col">
                  <div className="detail-card">
                    <span className="detail-label">From</span>
                    <strong>{document.from?.name}</strong>
                    {document.from?.businessName && <p>{document.from.businessName}</p>}
                    {document.from?.email && <p>{document.from.email}</p>}
                  </div>
                  <div className="detail-card">
                    <span className="detail-label">Bill To</span>
                    <strong>{document.to?.name}</strong>
                    {document.to?.businessName && <p>{document.to.businessName}</p>}
                    {document.to?.email && <p>{document.to.email}</p>}
                  </div>
                </div>
              </div>

              {/* Items */}
              <div className="detail-section">
                <h3>Items</h3>
                <div className="detail-card">
                  <table className="items-detail-table">
                    <thead>
                      <tr>
                        <th>Description</th>
                        <th>Qty</th>
                        <th>Price</th>
                        <th>Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {document.items?.map((item, index) => (
                        <tr key={index}>
                          <td>{item.description}</td>
                          <td>{item.quantity}</td>
                          <td>{formatCurrency(item.unitPrice)}</td>
                          <td>{formatCurrency(item.amount)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Totals */}
              <div className="detail-section">
                <h3>Totals</h3>
                <div className="detail-card highlight">
                  <div className="detail-row">
                    <span className="detail-label">Subtotal</span>
                    <span>{formatCurrency(document.subtotal)}</span>
                  </div>
                  {document.taxRate > 0 && (
                    <div className="detail-row">
                      <span className="detail-label">Tax ({document.taxRate}%)</span>
                      <span>{formatCurrency(document.taxAmount)}</span>
                    </div>
                  )}
                  {document.discount > 0 && (
                    <div className="detail-row">
                      <span className="detail-label">Discount</span>
                      <span>-{formatCurrency(document.discountType === 'percentage' ? document.subtotal * document.discount / 100 : document.discount)}</span>
                    </div>
                  )}
                  <div className="detail-row total-row">
                    <span className="detail-label">Total</span>
                    <span className="detail-price">{formatCurrency(document.total)}</span>
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Notes */}
          {document.notes && (
            <div className="detail-section">
              <h3>Notes</h3>
              <div className="detail-card">
                <p>{document.notes}</p>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <div className="modal-actions-left">
            <button 
              className="btn btn-danger btn-sm" 
              onClick={onDelete}
              disabled={loading}
            >
              🗑️ Delete
            </button>
          </div>
          <div className="modal-actions-right">
            {(document.seller?.email || document.buyer?.email || document.from?.email || document.to?.email) && (
              <button 
                className="btn btn-secondary btn-sm" 
                onClick={onResendEmail}
                disabled={loading}
              >
                {loading ? '...' : '📧 Resend Email'}
              </button>
            )}
            <button className="btn btn-primary btn-sm" onClick={onDownload}>
              📥 Download PDF
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocumentDetailModal;
